/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LocaleCookieName } from "../types/locale.types";

/**
 * Cross-tab locale synchronization via the Cookie Store API.
 *
 * The `EPIC_LOCALE` cookie is the source of truth for the active locale, and
 * `@wf-components/navigation-consumer` writes it through `cookieStore` when a user
 * switches locale. `cookieStore` dispatches a `change` event in every same-origin
 * document whenever the cookie jar changes — whether the write came from
 * `cookieStore.set`, an HTTP `Set-Cookie` response, or another tab — so listening to
 * that event is all the wiring cross-tab sync needs: nothing is sent at switch time.
 *
 * Two edges worth knowing:
 * - The event also fires in the document that set the cookie. The switching tab is
 *   already navigating itself via the nav handler, so the two navigations coalesce:
 *   receivers compare against the locale the page was *rendered* with and fire at
 *   most once.
 * - Browsers without the Cookie Store API fire no event; those tabs simply don't
 *   sync. This matches the write path — the same browsers are where the nav falls
 *   back to silent `document.cookie` writes.
 *
 * Cross-DOMAIN locale propagation is a separate concern already handled by the
 * `?setlang=` query param appended to cross-domain links — this module only covers
 * multiple tabs of the same origin.
 *
 * Only {@link reloadIntoCookieLocale} is part of the package's public API; the rest
 * is internal wiring shared with `LocaleTabSync` (imported via the module path, not
 * the package barrel).
 */

/**
 * Normalizes a locale code for tolerant equality comparison: trims, lowercases, and
 * unifies the region separator (`pt_BR` and `pt-BR` compare equal). Genuinely
 * different locales (e.g. `en` vs `en-US`, `zh-Hans` vs `zh-Hant`) remain distinct,
 * so this only absorbs casing/separator skew between a cookie value and a
 * server-resolved locale — never collapses two real locales into one.
 */
export function normalizeLocale(locale: string | undefined | null): string {
	return (locale ?? "").trim().toLowerCase().replace(/_/g, "-");
}

/**
 * Returns `href` with the `lang` and `setlang` query params removed. Middleware gives
 * those params precedence over the `EPIC_LOCALE` cookie, so a sync-triggered reload
 * must drop them — otherwise the tab would be pinned to the previous locale and reload
 * again (without converging) on every subsequent signal. Returns `href` unchanged if it
 * is not an absolute URL.
 */
export function stripLocaleOverrideParams(href: string): string {
	try {
		const url = new URL(href);
		url.searchParams.delete("lang");
		url.searchParams.delete("setlang");
		return url.toString();
	} catch {
		return href;
	}
}

/**
 * Reloads the current document so it re-renders in the locale the `EPIC_LOCALE` cookie
 * now holds. Navigates via `location.replace` to the current href stripped of
 * `lang`/`setlang` overrides when any were present (see
 * {@link stripLocaleOverrideParams}), falling back to `location.reload()` when the URL
 * is unchanged — `replace()` with an identical URL that has a `#hash` would be a
 * same-document fragment navigation, not a reload. `replace` (vs `assign`) keeps the
 * pre-switch URL out of history, where Back would re-pin the old locale.
 */
export function reloadIntoCookieLocale(): void {
	const target = stripLocaleOverrideParams(window.location.href);
	if (target === window.location.href) {
		window.location.reload();
	} else {
		window.location.replace(target);
	}
}

/**
 * Subscribes to `EPIC_LOCALE` cookie changes and invokes `onLocaleChange` once when
 * the cookie changes to a locale different from `renderedLocale` (the locale the
 * current page was rendered with).
 *
 * The reload side effect is injected via `onLocaleChange` so the listener wiring stays
 * testable; {@link LocaleTabSync} passes {@link reloadIntoCookieLocale}.
 *
 * Comparison is against the *rendered* locale — not the previous cookie value —
 * because middleware can resolve a locale that differs from the raw cookie (legacy
 * code normalization, `?lang=` overrides). It is also normalized
 * ({@link normalizeLocale}) so casing/separator skew between the cookie and the
 * rendered locale does not cause spurious or missed reloads. The one-shot latch
 * collapses bursts of cookie writes (client set + backend `Set-Cookie`) into a single
 * callback; cookie *deletions* are ignored — middleware falls back to its own
 * resolution on the next real navigation.
 *
 * No-ops (returns a no-op cleanup) during SSR and in browsers without the Cookie
 * Store API.
 *
 * @returns A cleanup function that removes the listener.
 */
export function startLocaleSync(renderedLocale: string, onLocaleChange: () => void): () => void {
	const cookieStore = typeof window === "undefined" ? undefined : window.cookieStore;
	if (!cookieStore) {
		return () => {};
	}

	const baseline = normalizeLocale(renderedLocale);
	let triggered = false;

	// Typed via lib.dom's CookieStoreEventMap ("change" -> CookieChangeEvent), so no
	// cast is needed. The optional chains stay: real-world payloads can be sparser
	// than the lib types claim (older implementations, synthetic events).
	const handleChange = (event: CookieChangeEvent): void => {
		if (triggered) {
			return;
		}
		const localeCookie = event.changed?.find((cookie) => cookie?.name === LocaleCookieName);
		const incoming = normalizeLocale(localeCookie?.value);
		if (!incoming || incoming === baseline) {
			return;
		}
		triggered = true;
		onLocaleChange();
	};

	cookieStore.addEventListener("change", handleChange);

	return () => {
		cookieStore.removeEventListener("change", handleChange);
	};
}
